<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  chat: { type: Object, default: () => ({}) },
});

const store = useStore();
const pillRef = ref(null);
const openMenu = ref(false);
const isLoading = ref(false);

// ── Status config ──
const statusConfig = {
  open: { label: 'Abierto', color: '#00a884' },
  pending: { label: 'Pendiente', color: '#f59e0b' },
  snoozed: { label: 'Posponer', color: '#3b82f6' },
  resolved: { label: 'Resuelto', color: '#8696a0' },
};

const statusOptions = ['open', 'pending', 'snoozed', 'resolved'];

// ── Computed ──
const currentChat = computed(() => store.getters.getSelectedChat);
const currentStatus = computed(() => currentChat.value?.status || 'open');
const statusInfo = computed(() => statusConfig[currentStatus.value] || statusConfig.open);

// ── Actions ──
async function changeStatus(newStatus) {
  if (newStatus === currentStatus.value) {
    openMenu.value = false;
    return;
  }

  // Snoozed: open the native Chatwoot snooze modal (lets user pick when)
  if (newStatus === 'snoozed') {
    openMenu.value = false;
    const ninja = document.querySelector('ninja-keys');
    if (ninja) ninja.open({ parent: 'snooze_conversation' });
    return;
  }

  isLoading.value = true;
  openMenu.value = false;

  try {
    await store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: newStatus,
    });
  } catch (e) {
    // silently fail
  } finally {
    isLoading.value = false;
  }
}

// ── Click outside handler ──
function onClickOutside(e) {
  if (pillRef.value && !pillRef.value.contains(e.target)) {
    openMenu.value = false;
  }
}

function toggle() {
  openMenu.value = !openMenu.value;
}

// ── Lifecycle ──
onMounted(() => {
  document.addEventListener('click', onClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside);
});
</script>

<template>
  <div ref="pillRef" class="hw-pill-wrap">
    <button
      class="hw-pill hw-conv-status"
      :style="{ borderColor: statusInfo.color }"
      :disabled="isLoading"
      @click.stop="toggle"
    >
      <span class="hw-status-dot" :style="{ background: statusInfo.color }" />
      <span class="hw-label">{{ statusInfo.label }}</span>
      <svg class="hw-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
    </button>
    <!-- Status dropdown -->
    <div v-if="openMenu" class="hw-dropdown" @click.stop>
      <div class="hw-dd-title">Estado Chatwoot</div>
      <button
        v-for="key in statusOptions"
        :key="key"
        class="hw-dd-item"
        :class="{ active: key === currentStatus }"
        @click="changeStatus(key)"
      >
        <span class="hw-status-dot" :style="{ background: statusConfig[key].color }" />
        {{ statusConfig[key].label }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.hw-pill-wrap {
  position: relative;
}

.hw-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 12px;
  background: transparent;
  color: rgb(var(--slate-10));
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}
.hw-pill:hover {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
}
.hw-pill:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hw-conv-status {
  border-width: 1.5px;
  color: rgb(var(--slate-12));
}

.hw-chevron {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  opacity: 0.5;
}

.hw-label {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hw-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Dropdown */
.hw-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-4));
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  z-index: 100;
  overflow: hidden;
}

.hw-dd-title {
  padding: 6px 12px 4px;
  font-size: 10px;
  font-weight: 600;
  color: rgb(var(--slate-10));
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgb(var(--slate-4));
}

.hw-dd-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  color: rgb(var(--slate-12));
  cursor: pointer;
  font-size: 12px;
  text-align: left;
}
.hw-dd-item:hover {
  background: rgb(var(--slate-3));
}
.hw-dd-item.active {
  color: rgb(var(--blue-11));
  font-weight: 600;
}
</style>
